import { useQuery } from '@tanstack/react-query';
import { gitApi, gitKeys } from './api';
import { type GitPermissions, useGitPermissions } from './use-git-permissions';

/** Dự án này đã nối Git chưa, nhìn từ người đang xem — APM-GIT-q. */
export interface ProjectGitConnection {
  /**
   * `false` khi chưa trả lời được: quyền chưa về, hoặc có `git:read` mà danh sách
   * kho còn đang tải. Nơi dùng **không vẽ gì** lúc này — không khung rỗng nháy lên.
   */
  readonly known: boolean;
  /** Có ít nhất một kho đang gắn (`git_repository.archived_at IS NULL`) **và** người xem có `git:read`. */
  readonly connected: boolean;
  /** Quyền git ở dự án — trả kèm để nơi dùng khỏi hỏi lần hai. */
  readonly permissions: GitPermissions;
}

/**
 * "Dự án đã nối Git" = có ít nhất một kho đang gắn — APM-GIT-q (người dùng chốt
 * 2026-10-12: dự án chưa nối Git thì không hiện các phần liên quan tới Git ngoài tab Git).
 *
 * Một hook dùng chung cho mọi chỗ hiện phần Git bên ngoài tab Git: khối "Phát
 * triển" của task / issue, dấu MR trên thẻ Kanban. Đọc `GET …/git/repositories`
 * (mặc định chỉ kho chưa gỡ) qua **đúng khoá** `gitKeys.repositories` mà tab Git,
 * form gắn link tay và Cấu hình dự án đang dùng — mở task sau khi đã xem tab Git
 * không bắn thêm lời gọi, và gắn / gỡ kho (vốn `invalidate` khoá ấy) làm mới luôn
 * câu trả lời ở đây.
 *
 * Không có `git:read` ⇒ `connected: false` và **không lời gọi nào** (giữ hành vi quyền
 * của `useGitPermissions`). Lỗi tải danh sách kho ⇒ coi như chưa nối: hỏng về phía ẩn,
 * như phần còn lại của tính năng.
 *
 * ⚠️ Không nhận asOf (luật #7): "đã nối" là trạng thái **hiện tại** của cấu hình, không
 * phải dữ liệu theo thời gian. Nơi dùng vẫn đọc dữ liệu git theo mốc của nó; hook này
 * chỉ quyết định có vẽ phần Git hay không.
 */
export function useProjectGitConnected(projectId: string): ProjectGitConnection {
  const permissions = useGitPermissions(projectId);
  const repositories = useQuery({
    queryKey: gitKeys.repositories(projectId),
    queryFn: () => gitApi.repositories(projectId),
    enabled: permissions.read,
    retry: false,
  });

  if (!permissions.read) return { known: permissions.known, connected: false, permissions };
  if (repositories.isPending) return { known: false, connected: false, permissions };
  // Lỗi mà chưa từng có dữ liệu ⇒ 0 kho. Lỗi lúc làm mới thì giữ câu trả lời đã có.
  const connected = (repositories.data?.length ?? 0) > 0;
  return { known: true, connected, permissions };
}
